import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import LoadingIndicator from '@/components/LoadingIndicator'
import { useLazyQuery } from '@apollo/client'
import { Close, Info } from '@mui/icons-material'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import OrderedItemCard from './ItemCard/OrderedItemCard'
import OrderedItemDetail from './ItemDetail/OrderedItemDetail'
import { ccDashboardCancelOrder } from '@/legacyApi/ccOrder'

const canteenOrderedItemsMenuDataFragment = graphql(`
  fragment CanteenOrderedItems_MenuDataFragment on CCMealService {
    orderedItems(input: { acrossAllLocations: true }) {
      items {
        id
        ...CanteenOrderedItemCard_OrderItemFragment
      }
    }
  }
`)

const canteenOrderedItemsUserDataFragment = graphql(`
  fragment CanteenOrderedItems_UserDataFragment on User {
    ...CanteenOrderedItemCard_UserDataFragment
  }
`)

const canteenOrderedItemsItemDataQuery = graphql(`
  query CanteenOrderedItemDetail_ItemDataQuery($id: ID!) {
    ccOrderItem(id: $id) {
      id
      ...CanteenOrderedItemDetail_ItemDataFragment
    }
  }
`)

type Props = {
  menuData: FragmentType<typeof canteenOrderedItemsMenuDataFragment>
  userData: FragmentType<typeof canteenOrderedItemsUserDataFragment>
  onSuccessfulCancellation: () => Promise<void>
}

const OrderedItemsGraphql: React.FC<Props> = ({
  menuData: menuDataProp,
  userData: userDataProp,
  onSuccessfulCancellation,
}) => {
  const menuData = useFragment(canteenOrderedItemsMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenOrderedItemsUserDataFragment, userDataProp)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [itemsPendingCancellation, setItemsPendingCancellation] = useState<Set<number>>(new Set())
  const [itemToBeCancelled, setItemToBeCancelled] = useState<number | null>(null)
  const [selectedItemId, setSelectedItemId] = useState('')
  const [getItemData, { data: selectedItemData, loading: loadingItem }] = useLazyQuery(
    canteenOrderedItemsItemDataQuery,
  )

  useEffect(() => {
    if (!menuData.orderedItems.items.length || !itemsPendingCancellation.size) return
    const itemsBeingCancelled = Array.from(itemsPendingCancellation)
    const itemsSuccessfullyCancelled = itemsBeingCancelled.reduce<number[]>(
      (cancelledItems, itemPending) => {
        const itemWasCancelled = !menuData.orderedItems.items.some(
          (item) => item.id === itemPending,
        )
        return [...cancelledItems, ...(itemWasCancelled ? [itemPending] : [])]
      },
      [],
    )

    if (!itemsSuccessfullyCancelled.length) return
    setItemsPendingCancellation((prev) => {
      const newState = new Set(prev)
      itemsSuccessfullyCancelled.forEach((itemPendingCancellation) => {
        newState.delete(itemPendingCancellation)
      })
      return newState
    })
  }, [itemsPendingCancellation, menuData.orderedItems.items])

  const handleSeeMore = (orderItemId: string) => {
    setSelectedItemId(orderItemId)
    getItemData({ variables: { id: orderItemId } })
  }

  const handleCloseItemDialog = () => {
    setSelectedItemId('')
  }

  const handleCancelItem = (itemId: number) => {
    setItemToBeCancelled(itemId)
  }

  const handleDeclineCancellation = () => {
    setItemToBeCancelled(null)
  }

  const handleConfirmCancelation = async (itemId?: number) => {
    if (selectedItemId) setSelectedItemId('')
    const itemIdForCancellation = itemId ?? itemToBeCancelled
    if (itemIdForCancellation) {
      setItemsPendingCancellation((prev) => {
        const newState = new Set(prev)
        newState.add(itemIdForCancellation)
        return newState
      })
      setItemToBeCancelled(null)
      await ccDashboardCancelOrder(itemIdForCancellation)
      onSuccessfulCancellation()
    }
  }

  return menuData.orderedItems.items.length > 0 ? (
    <>
      <Dialog
        onClose={() => handleDeclineCancellation()}
        open={Boolean(itemToBeCancelled && !selectedItemId)}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Info color="warning" />
            <Typography>Are you sure you want to cancel this item?</Typography>
          </Stack>
        </DialogTitle>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleConfirmCancelation()
            }}
          >
            Cancel it
          </Button>
          <Button onClick={() => handleDeclineCancellation()}>Keep it</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        sx={{
          '.MuiPaper-root': {
            height: { md: '93vh', xs: '100%' },
            minHeight: { md: '93vh', xs: '100%' },
            overflow: 'scroll',
          },
        }}
        fullScreen={fullScreen}
        onClose={handleCloseItemDialog}
        maxWidth="md"
        fullWidth
        open={Boolean(selectedItemId)}
      >
        <IconButton onClick={handleCloseItemDialog} sx={{ position: 'absolute', right: 0 }}>
          <Close />
        </IconButton>
        {loadingItem ? <LoadingIndicator /> : null}
        {selectedItemData && !loadingItem ? (
          <OrderedItemDetail
            cancelItem={(itemId) => {
              handleConfirmCancelation(itemId)
            }}
            itemData={selectedItemData.ccOrderItem}
          />
        ) : null}
      </Dialog>
      <Grid spacing={1} container alignItems="stretch">
        <Grid item xs={12}>
          <Typography variant="subtitle2">Ordered items</Typography>
        </Grid>
        {menuData.orderedItems.items.map((item) => (
          <Grid md={6} lg={6} xl={4} xs={12} key={item.id} item>
            <OrderedItemCard
              pendingCancellation={itemsPendingCancellation.has(item.id)}
              showItemDetails={() => handleSeeMore(item.id.toString())}
              userData={userData}
              cancelOrder={handleCancelItem}
              orderItem={item}
            />
          </Grid>
        ))}
      </Grid>
    </>
  ) : null
}

export default OrderedItemsGraphql
